import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { MasajidContext } from "../contexts/MasajidContext";
import {
  getCurrentLocation,
  sortMasajidByDistance,
} from "../utils/locationUtils";

function NearbyMasajid() {
  const { masajid: allMasajid, language } = useContext(MasajidContext);
  const masajid = useMemo(
    () => allMasajid?.filter((masjid) => !masjid.isPrivate) ?? [],
    [allMasajid],
  );
  const [nearbyMasajid, setNearbyMasajid] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const navigate = useNavigate();

  const handleFindNearby = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const location = await getCurrentLocation();
      setUserLocation(location);

      const sortedMasajid = sortMasajidByDistance(
        masajid,
        location.latitude,
        location.longitude,
      );

      const validMasajid = sortedMasajid
        .filter((masjid) => masjid.distance !== Infinity)
        .slice(0, 7);

      setNearbyMasajid(validMasajid);
    } catch (err) {
      setError(err.message);
      setUserLocation(null);
    } finally {
      setLoading(false);
    }
  }, [masajid]);

  useEffect(() => {
    handleFindNearby();
  }, [handleFindNearby]);

  const handleViewDetails = (masjid) => {
    navigate(`/masjid/${masjid.username}`);
  };

  const formatDistance = (distance) => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m`;
    }
    return `${distance.toFixed(1)}km`;
  };

  return (
    <>
      <div
        style={{
          color: "#ffffff",
          marginTop: 16,
        }}
        lang={language === "urdu" && "ur"}
      >
        <h3 className="card-title" style={{ textAlign: "center" }}>
          {language === "urdu" ? "قریبی مساجد" : "Nearby Masajid"}
        </h3>
      </div>

      {loading && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <div
            className="spinner-border"
            role="status"
            style={{ color: "rgb(27, 179, 200)" }}
          >
            <span className="sr-only">Loading...</span>
          </div>
          <p
            style={{
              marginTop: 10,
              color: "rgb(255, 255, 255, 0.8)",
            }}
            lang={language === "urdu" && "ur"}
          >
            {language === "urdu" ? "لوڈنگ" : "Getting your location..."}
          </p>
        </div>
      )}

      {error && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ color: "#f63c3c", marginBottom: 10 }}>{error}</p>
          <button
            onClick={handleFindNearby}
            style={{
              backgroundColor: "rgb(27, 179, 200)",
              border: "none",
              borderRadius: "20px",
              lang: language === "urdu" && "ur",
              padding: "8px 16px",
              color: "#ffffff",
              fontSize: "0.9rem",
              cursor: "pointer",
            }}
          >
            {language === "urdu" ? "دوبارہ کوشش کریں" : "Try Again"}
          </button>
        </div>
      )}

      {nearbyMasajid.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: 20,
          }}
        >
          {userLocation && (
            <>
              <p
                style={{
                  textAlign: "center",
                  color: "rgb(255, 255, 255, 0.6)",
                  fontSize: "0.9rem",
                  marginBottom: 5,
                }}
                lang={language === "urdu" && "ur"}
              >
                {language === "urdu"
                  ? "آپ کی لوکیشن سے قریب ترین مساجد"
                  : "Showing nearest Masajid to your location"}
              </p>
              <div
                style={{
                  textAlign: "center",
                  marginBottom: 18,
                }}
              >
                <button
                  onClick={handleFindNearby}
                  style={{
                    backgroundColor: "transparent",
                    border: "1px solid rgb(27, 179, 200)",
                    borderRadius: "15px",
                    padding: "6px 12px",
                    color: "rgb(27, 179, 200)",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                  }}
                  lang={language === "urdu" && "ur"}
                >
                  {language === "urdu"
                    ? "لوکیشن ریفریش کریں"
                    : "Refresh Location"}
                </button>
              </div>

              {userLocation.accuracy > 100 && (
                <div
                  style={{
                    backgroundColor: "#f63c3c20",
                    border: "2px solid #f63c3c",
                    borderRadius: "12px",
                    padding: "8px 12px",
                    marginBottom: 10,
                    width: "95%",
                    maxWidth: "800px",
                    textAlign: "center",
                  }}
                >
                  <span
                    style={{
                      color: "#f63c3c",
                      fontSize: "0.85rem",
                      fontWeight: "500",
                      lang: language === "urdu" && "ur",
                    }}
                  >
                    {language === "urdu"
                      ? "غیر مستند لوکیشن - فاصلے غلط ہو سکتے ہیں"
                      : "Inaccurate location - distances may be off"}
                  </span>
                </div>
              )}

              {nearbyMasajid.map((masjid, index) => (
                <div
                  key={masjid.username}
                  className="d-flex justify-content-center fade-in"
                  onClick={() => handleViewDetails(masjid)}
                  style={{
                    animationDelay: `${index * 50}ms`,
                    background: "radial-gradient(circle, #0a3545, #041b23)",
                    borderRadius: "12px",
                    padding: "12px",
                    marginTop: "10px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    border: "3px solid rgb(27, 179, 200, 0.3)",
                    width: "95%",
                    maxWidth: "800px",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection:
                          language === "urdu" ? "row-reverse" : "row",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          textAlign: language === "urdu" ? "right" : "left",
                          minWidth: 0,
                        }}
                      >
                        <h6
                          style={{
                            margin: "0 0 4px 0",
                            color: "#ffffff",
                            fontSize: "1rem",
                            textAlign: language === "urdu" ? "right" : "left",
                          }}
                          lang={language === "urdu" && "ur"}
                        >
                          {language === "urdu"
                            ? masjid.urdName
                            : masjid.engName}
                        </h6>
                      </div>
                      <div
                        style={{
                          backgroundColor: "rgb(27, 179, 200)",
                          borderRadius: "15px",
                          padding: "4px 8px",
                          marginLeft: language === "urdu" ? "0" : "10px",
                          marginRight: language === "urdu" ? "10px" : "0",
                          minWidth: "60px",
                          textAlign: "center",
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.8rem",
                            fontWeight: "bold",
                            color: "#ffffff",
                          }}
                        >
                          {formatDistance(masjid.distance)}
                        </span>
                      </div>
                    </div>
                    <p
                      style={{
                        marginBottom: 12,
                        color: "rgb(255, 255, 255, 0.6)",
                        fontSize: "0.8rem",
                        textAlign: language === "urdu" ? "right" : "left",
                        maxWidth: "73%",
                        marginLeft: language === "urdu" ? "auto" : "0",
                      }}
                      lang={language === "urdu" && "ur"}
                      dir={language === "urdu" ? "rtl" : "ltr"}
                    >
                      {language === "urdu"
                        ? masjid.addressUrdu
                        : masjid.address}
                    </p>
                    <p
                      style={{
                        fontSize: "0.8rem",
                        marginBottom: 0,
                        color: "rgb(7,240,255)",
                        cursor: "pointer",
                        textAlign: "center",
                      }}
                      lang={language === "urdu" && "ur"}
                    >
                      {language === "urdu"
                        ? "تفصیلات دیکھنے کے لیے یہاں دبائیں"
                        : "Click to view details"}
                    </p>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}

export default NearbyMasajid;
